<template>
  <div
    class="font-inter w-full h-[1040px] bg-[#FFFFFF] rounded-t-[20px] overflow-hidden"
  >
  <div v-if="isLoading" class="flex justify-center items-center py-20">
    <div
      class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primaryDarkBg"
    ></div>
  </div>  
    <div v-else-if="!isLoading && project.project_id" class="font-inter h-full overflow-auto">
      <div v-if="isAdmin || isTalent || isManager" class=" "></div>
      <div class="my-4 mx-2 justify-between px-6">
        <div class="flex flex-col lg:flex-row gap-8 lg:gap-16">
          <div class="space-y-4 flex-1 max-w-full lg:max-w-[490px] text-[15px]">
            <div class="flex items-center justify-between mb-2">
              <p class="font-medium text-primaryDarkBg">About Project</p>
              <EditIcon
                v-if="isAdmin || isManager"
                @click="toggleEditDescription"
                class="cursor-pointer w-4 h-4 text-gray-500 hover:text-primaryDark"
              />
            </div>

            <span class="space-y-3 text-grayDark">
              <div v-if="!isEditingDescription">
                <p class="break-words">{{ project.description }}.</p>
              </div>
              <div v-else class="space-y-2">
                <textarea
                  v-model="editedDescription"
                  class="w-full p-2 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-primaryDark"
                  rows="4"
                  placeholder="Enter project description..."
                ></textarea>
                <div class="flex gap-2">
                  <button
                    @click="saveDescription"
                    class="px-3 py-1 bg-primaryDark text-white rounded text-sm hover:bg-opacity-90"
                  >
                    Save
                  </button>
                  <button
                    @click="cancelEditDescription"
                    class="px-3 py-1 bg-gray-300 text-gray-700 rounded text-sm hover:bg-gray-400"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </span>
          </div>
          <div class="space-y-8 text-grayDark text-[13px] flex-1 min-w-0">
            <div class="space-y-4">
              <div class="flex items-center justify-between">
                <p class="font-medium text-primaryDarkBg text-[15px]">Links</p>
                <EditIcon
                  v-if="isAdmin || isManager"
                  @click="toggleEditLinks"
                  class="cursor-pointer w-4 h-4 text-gray-500 hover:text-primaryDark"
                />
              </div>

              <div v-if="!isEditingLinks" class="space-y-3">
                <div class="space-y-1">
                  <div class="flex items-center gap-3">
                    <p class="font-medium">Website</p>
                    <div class="flex items-center gap-2">
                      <a
                        :href="project.website || '#'"
                        class="text-primaryDark underline hover:text-primaryDarkBg"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {{ project.website || "No website added" }}
                      </a>
                      <img
                        v-if="project.website"
                        src="@/assets/settings/arrow-up-right.svg"
                        alt="external link"
                        class="w-3 h-3 flex-shrink-0"
                      />
                    </div>
                  </div>
                </div>

                <div class="space-y-1">
                  <div class="flex items-center gap-3">
                    <p class="font-medium">Email</p>
                    <div class="flex items-center gap-2">
                      <a
                        :href="`mailto:${project.email || ''}`"
                        class="text-primaryDark underline hover:text-primaryDarkBg"
                      >
                        {{ project.email || "No email added" }}
                      </a>
                      <img
                        v-if="project.email"
                        src="@/assets/settings/arrow-up-right.svg"
                        alt="external link"
                        class="w-3 h-3 flex-shrink-0"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div v-else class="space-y-4">
                <div class="space-y-2">
                  <label class="font-medium">Website</label>
                  <input
                    v-model="editedWebsite"
                    type="url"
                    class="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primaryDark"
                    placeholder="https://example.com"
                  />
                </div>

                <div class="space-y-2">
                  <label class="font-medium">Email</label>
                  <input
                    v-model="editedEmail"
                    type="email"
                    class="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primaryDark"
                    placeholder="contact@example.com"
                  />
                </div>

                <div class="flex gap-2">
                  <button
                    @click="saveLinks"
                    class="px-3 py-1 bg-primaryDark text-white rounded text-sm hover:bg-opacity-90"
                  >
                    Save
                  </button>
                  <button
                    @click="cancelEditLinks"
                    class="px-3 py-1 bg-gray-300 text-gray-700 rounded text-sm hover:bg-gray-400"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>

            <div class="space-y-2">
              <div class="flex items-center justify-between">
                <p class="font-medium">Development Stack</p>
                <EditIcon
                  v-if="isAdmin || isManager"
                  @click="toggleEditTechStacks"
                  class="cursor-pointer w-4 h-4 text-gray-500 hover:text-primaryDark"
                />
              </div>
              
              <div v-if="!isEditingTechStacks" class="flex flex-wrap gap-2">
                <div
                  v-for="(stack, index) in project?.tech_stack"
                  :key="index"
                  class="text-[12px]"
                >
                  <p
                    class="px-3 py-1 rounded-lg text-center truncate h-[23px] flex items-center justify-center inline-block w-fit"
                    :style="{
                      backgroundColor: getRandomStackColor(),
                      color: '#667085',
                    }"
                  >
                    {{ stack }}
                  </p>
                </div>
              </div>
              
              <div v-else class="space-y-4">
                <div class="flex flex-wrap gap-2">
                  <div
                    v-for="(stack, index) in editedTechStacks"
                    :key="index"
                    class="flex items-center gap-2 px-2 py-1 bg-gray-100 rounded-lg"
                  >
                    <input
                      v-model="editedTechStacks[index]"
                      type="text"
                      class="bg-transparent text-[12px] border-none outline-none min-w-0 flex-1"
                      :style="{ color: '#667085' }"
                      placeholder="Tech stack..."
                    />
                    <button
                      @click="removeTechStack(index)"
                      class="text-red-500 hover:text-red-700 text-sm ml-1"
                    >
                      ×
                    </button>
                  </div>
                  
                  <button
                    @click="addTechStack"
                    class="px-3 py-1 border-2 border-dashed border-gray-300 rounded-lg text-[12px] text-gray-500 hover:border-primaryDark hover:text-primaryDark"
                  >
                    + Add Stack
                  </button>
                </div>
                
                <div class="flex gap-2">
                  <button
                    @click="saveTechStacks"
                    class="px-3 py-1 bg-primaryDark text-white rounded text-sm hover:bg-opacity-90"
                  >
                    Save
                  </button>
                  <button
                    @click="cancelEditTechStacks"
                    class="px-3 py-1 bg-gray-300 text-gray-700 rounded text-sm hover:bg-gray-400"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
            <div class="space-y-2">
              <p class="font-medium">Project Progress</p>
              <div class="flex items-center">
                <div
                  class="relative w-full max-w-[332px] h-[9px] bg-[#F3F4F6] rounded-full overflow-hidden"
                >
                  <!-- Inner progress bar -->
                  <div
                    class="absolute top-0 left-0 h-full bg-primaryDark rounded-full"
                    :style="{ width: `${project.progress}%` }"
                  ></div>
                </div>
                <span class="ml-2 text-[12px] font-medium flex-shrink-0"
                  >{{ project.progress }}%</span
                >
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import apiClient from "@/axios";

import EditIcon from "../svg-icons/EditIcon.vue";
export default {
  name: "AboutProject",
  components: {
    EditIcon,
  },
  data() {
    return {
      isEditingDescription: false,
      isEditingLinks: false,
      isEditingTechStacks: false,
      editedDescription: "",
      editedWebsite: "",
      editedEmail: "",
      editedTechStacks: [],
      isLoading: false,
      projectName: "",
      project: {
        start_date: "",
        end_date: "",
        project_id: "",
        project_name: "",
        description: "",
        website: "",
        email: "",
      },
    };
  },
  watch: {},
  computed: {
    isAdmin() {
      return this.$store.getters.isAdmin;
    },
    isManager() {
      return this.$store.getters.isManager;
    },
    isTalent() {
      return this.$store.getters.isTalent;
    },
    isInvestor() {
      return this.$store.getters.isInvestor;
    },
  },
  methods: {
    toggleEditDescription() {
      this.isEditingDescription = !this.isEditingDescription;
      if (this.isEditingDescription) {
        this.editedDescription = this.project.description;
      }
    },

    cancelEditDescription() {
      this.isEditingDescription = false;
      this.editedDescription = "";
    },

    async saveDescription() {
      try {
        const projectId = this.$route.params.projectId;
        await apiClient.patch(`/web/projects/${projectId}`, {
          description: this.editedDescription,
        });

        this.project.description = this.editedDescription;
        this.isEditingDescription = false;
        this.$toast.success("Project description updated successfully!");
      } catch (error) {
        console.error("Error updating description:", error);
        this.$toast.error("Failed to update description. Please try again.");
      }
    },

    toggleEditLinks() {
      this.isEditingLinks = !this.isEditingLinks;
      if (this.isEditingLinks) {
        this.editedWebsite = this.project.website || "";
        this.editedEmail = this.project.email || "";
      }
    },

    cancelEditLinks() {
      this.isEditingLinks = false;
      this.editedWebsite = "";
      this.editedEmail = "";
    },

    async saveLinks() {
      try {
        const projectId = this.$route.params.projectId;
        await apiClient.patch(`/web/projects/${projectId}`, {
          website: this.editedWebsite,
          email: this.editedEmail,
        });

        this.project.website = this.editedWebsite;
        this.project.email = this.editedEmail;
        this.isEditingLinks = false;
        this.$toast.success("Project links updated successfully!");
      } catch (error) {
        console.error("Error updating links:", error);
        this.$toast.error("Failed to update links. Please try again.");
      }
    },

    showSuccessMessage(message) {
      this.$toast.success(message);
    },

    showErrorMessage(message) {
      console.error(message);
      this.$toast.error(message);
    },

    toggleEditTechStacks() {
      this.isEditingTechStacks = !this.isEditingTechStacks;
      if (this.isEditingTechStacks) {
        this.editedTechStacks = [...(this.project.tech_stack || [])];
      }
    },

    addTechStack() {
      this.editedTechStacks.push('');
    },

    removeTechStack(index) {
      this.editedTechStacks.splice(index, 1);
    },

    cancelEditTechStacks() {
      this.isEditingTechStacks = false;
      this.editedTechStacks = [];
    },

    async saveTechStacks() {
      try {
        const filteredStacks = this.editedTechStacks.filter(stack => stack.trim() !== '');
        
        const projectId = this.$route.params.projectId;
        await apiClient.patch(`/web/projects/${projectId}`, {
          tech_stack: filteredStacks
        });
        
        this.project.tech_stack = filteredStacks;
        this.isEditingTechStacks = false;
        this.editedTechStacks = [];
        this.$toast.success('Development stack updated successfully!');
      } catch (error) {
        console.error("Error updating tech stacks:", error);
        this.$toast.error('Failed to update development stack. Please try again.');
      }
    },

    getRandomStackColor() {
      const colors = [
        "rgba(50, 247, 47, 0.1)",
        "rgba(255, 179, 65, 0.13)",
        "rgba(102, 112, 133, 0.06)",
        "rgba(47, 109, 247, 0.06)",
        "rgba(255, 102, 102, 0.06)",
        "#F1FFF0",
        "#FEDBFF",
      ];
      return colors[Math.floor(Math.random() * colors.length)];
    },

    async fetchProject() {
      this.isLoading = true;
      const projectId = this.$route.params.projectId;
      try {
        const response = await apiClient.get(`/web/projects/${projectId}`);
        this.project = response.data.data;
        this.projectName = this.project.project_name;
      } catch (error) {
        if (error.response && error.response.status === 404) {
          this.$router.push({ name: "NotFound" });
        } else {
          console.error("Error fetching project data:", error);
        }
      } finally {
        this.isLoading = false;
      }
    },
  },
  created() {
    this.fetchProject();
  },
  mounted() {
    document.addEventListener("click", this.closeDropdown);
    document.addEventListener("click", this.closeStatusDropdown);
  },
  beforeUnmount() {
    document.removeEventListener("click", this.closeDropdown);
    document.removeEventListener("click", this.closeStatusDropdown);
  },
};
</script>

<style scoped>
.smoke-light {
  background-color: rgba(0, 0, 0, 0.5);
}
</style>
